import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useState } from 'react';

import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import { DatePicker, StaticDatePicker } from '@mui/x-date-pickers-pro';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { useAppSelector } from 'hooks/redux';

/**
 * Transforms an object with string keys and returns a new object
 * where each character per key is assinged the value of the key.
 */
const flattenObjectKeys = (obj) => {
  const newObject = {};
  Object.entries(obj).forEach(([key, value]) => {
    Array.from(key).forEach((subkey) => {
      newObject[subkey] = value;
    });
  });

  return newObject;
};

// otherwise Monday is considered the first day of the week
const LUXON_OPTIONS = { useLocaleWeeks: true };

const keyboardShortcutCallbackMap = flattenObjectKeys({
  '+=': (date) => date.plus({ days: 1 }),
  '-_': (date) => date.minus({ days: 1 }),
  rR: (date) => (date.month === 12 && date.day === 31 ? date.plus({ years: 1 }) : date.set({ month: 12, day: 31 })),
  yY: (date) => (date.month === 1 && date.day === 1 ? date.minus({ years: 1 }) : date.set({ month: 1, day: 1 })),
  hH: (date) => {
    const lastDayOfMonth = date.endOf('month').day;
    return date.day === lastDayOfMonth ? date.plus({ months: 1 }).endOf('month') : date.set({ day: lastDayOfMonth });
  },
  wW: (date) => (date.localWeekday === 1 ? date.minus({ weeks: 1 }) : date.startOf('week', LUXON_OPTIONS)),
  kK: (date) => (date.localWeekday === 7 ? date.plus({ weeks: 1 }) : date.endOf('week', LUXON_OPTIONS)),
  mM: (date) => (date.day === 1 ? date.minus({ months: 1 }) : date.startOf('month')),
  '.>': (date) => date.plus({ days: 7 }),
  ',<': (date) => date.minus({ days: 7 }),
  tT: (_) => DateTime.now(),
});

const SDatePicker = React.forwardRef((props, ref) => {
  const {
    value,
    onChange,
    onClose,
    onError,
    textFieldProps,
    id,
    inputFormat = 'MM/dd/yyyy',
    error,
    helperText,
    plain = false,
    plainProps,
    minDate = DateTime.local(1900, 1, 1),
    maxDate = DateTime.local(2099, 12, 31),
    clearable,
    anchorElProp,
    keyboardShortcutsEnabled = true,
    ...otherProps
  } = props;

  const [localError, setLocalError] = useState();
  const [anchorEl, setAnchorEl] = useState(anchorElProp ?? null);
  const theme = useTheme();

  const dateKeyboardShortcutsFf = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('dateKeyboardShortcuts'),
  );

  const date = value?.isLuxonDateTime ? value : DateTime.fromISO(value);

  const handleError = (errorString) => {
    let errorMessage;

    switch (errorString) {
      case 'minDate':
        errorMessage = `Date must be on or after ${minDate.toFormat('D')}`;
        break;
      case 'maxDate':
        errorMessage = `Date must be on or before ${maxDate.toFormat('D')}`;
        break;
      case 'invalid':
        errorMessage = 'Date is invalid';
        break;
      default:
        break;
    }

    setLocalError(errorMessage);

    if (onError) {
      onError(errorMessage);
    }
  };

  function handleKeyPress(e) {
    if (!dateKeyboardShortcutsFf) return;

    if (!keyboardShortcutsEnabled) return;

    const callback = keyboardShortcutCallbackMap[e.key];

    if (callback) {
      e.preventDefault();
      onChange(callback(date.c === null ? DateTime.now() : date));
    }
  }

  return (
    <>
      {plain ? (
        <>
          <ButtonBase
            {...plainProps}
            ref={ref}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.target);
            }}
          >
            {date.toFormat(inputFormat)}
          </ButtonBase>

          <>
            {anchorEl && (
              <Popover
                open
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                onClose={() => {
                  setAnchorEl(null);
                }}
                onKeyDown={handleKeyPress}
              >
                <StaticDatePicker
                  id={`${id}-picker-root`}
                  value={date}
                  onChange={(newDate) => {
                    onChange(newDate);
                    setAnchorEl(null);
                  }}
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                    actionBar: {
                      hidden: true,
                    },
                  }}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </Popover>
            )}
          </>
        </>
      ) : (
        <DatePicker
          id={id}
          label={inputFormat}
          format={inputFormat}
          // mask="YYYY-MM-DD"
          //  only apply mask as SDatePicker prop when you need a variable width input prop as it breaks auto-format
          value={date.isValid ? date : null}
          onChange={onChange}
          referenceDate={minDate}
          onClose={(e) => {
            onClose?.(e);
          }}
          minDate={minDate}
          maxDate={maxDate}
          onError={handleError}
          slots={{
            openPickerIcon: CalendarMonthRoundedIcon,
          }}
          slotProps={{
            field: {
              clearable: clearable,
            },
            textField: {
              id,
              onKeyDown: handleKeyPress,
              autoComplete: 'off',
              sx: {
                svg: { color: theme.palette.text.secondary },
              },
              variant: 'filled',
              error: error || !!localError,
              helperText: helperText || localError,
              ...textFieldProps,
              inputProps: {
                placeholder: 'MM/DD/YYYY',
                ...textFieldProps?.inputProps,
              },
            },
            popper: {
              onKeyDown: handleKeyPress, // required to capture keydown when date picker is opened
            },
          }}
          {...otherProps}
        />
      )}
    </>
  );
});

SDatePicker.displayName = 'SDatePicker';

SDatePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onClose: PropTypes.func,
  textFieldProps: PropTypes.object,
  id: PropTypes.string,
  inputFormat: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  plain: PropTypes.bool, // renders button for selection instead of text field
  plainProps: PropTypes.object,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  anchorElProp: PropTypes.object,
  clearable: PropTypes.bool,
  keyboardShortcutsEnabled: PropTypes.bool,
};

export default SDatePicker;
